import { queryOptions, useMutation } from '@tanstack/react-query'
import { dashboardKeys } from './keys'
import { CACHE_TIMES } from './cache-config'
import { studentDashboardApi } from '@/lib/api/student-dashboard'

export function profileQueryOptions() {
  return queryOptions({
    queryKey: dashboardKeys.profile(),
    queryFn: async () => {
      const { data, error } = await studentDashboardApi().api.student.dashboard.profile.get()
      if (error) throw error
      return data
    },
    staleTime: CACHE_TIMES.STALE_DEFAULT,
  })
}

export function statsQueryOptions() {
  return queryOptions({
    queryKey: dashboardKeys.stats(),
    queryFn: async () => {
      const { data, error } = await studentDashboardApi().api.student.dashboard.stats.get()
      if (error) throw error
      return data
    },
    staleTime: CACHE_TIMES.STALE_DEFAULT,
  })
}

export function quickStatsQueryOptions() {
  return queryOptions({
    queryKey: dashboardKeys.quickStats(),
    queryFn: async () => {
      const { data, error } = await studentDashboardApi().api.student.dashboard['quick-stats'].get()
      if (error) throw error
      return data
    },
    staleTime: CACHE_TIMES.STALE_DEFAULT,
  })
}

export function overallProgressQueryOptions() {
  return queryOptions({
    queryKey: dashboardKeys.overallProgress(),
    queryFn: async () => {
      const { data, error } = await studentDashboardApi().api.student.dashboard['overall-progress'].get()
      if (error) throw error
      return data
    },
    staleTime: CACHE_TIMES.STALE_DEFAULT,
  })
}

export function continueWatchingQueryOptions() {
  return queryOptions({
    queryKey: dashboardKeys.continueWatching(),
    queryFn: async () => {
      const { data, error } = await studentDashboardApi().api.student.dashboard['continue-watching'].get()
      if (error) throw error
      return data
    },
    staleTime: CACHE_TIMES.STALE_REALTIME,
  })
}

export function currentModuleQueryOptions() {
  return queryOptions({
    queryKey: dashboardKeys.currentModule(),
    queryFn: async () => {
      const { data, error } = await studentDashboardApi().api.student.dashboard['current-module'].get()
      if (error) throw error
      return data
    },
    staleTime: CACHE_TIMES.STALE_DEFAULT,
  })
}

export function moduleLessonsQueryOptions(moduleId: string) {
  return queryOptions({
    queryKey: dashboardKeys.moduleLessons(moduleId),
    queryFn: async () => {
      const { data, error } = await studentDashboardApi().api.student.dashboard['module-lessons']({ moduleId }).get()
      if (error) throw error
      return data
    },
    staleTime: CACHE_TIMES.STALE_DEFAULT,
    enabled: !!moduleId,
  })
}

export function enrolledCoursesQueryOptions(params?: { limit?: string | number; sortBy?: string }) {
  return queryOptions({
    queryKey: dashboardKeys.enrolledCourses(params),
    queryFn: async () => {
      const { data, error } = await studentDashboardApi().api.student.dashboard['enrolled-courses'].get({ query: params })
      if (error) throw error
      return data
    },
    staleTime: CACHE_TIMES.STALE_DEFAULT,
  })
}

export function recommendedCoursesQueryOptions(params?: { limit?: string | number }) {
  return queryOptions({
    queryKey: dashboardKeys.recommendedCourses(params),
    queryFn: async () => {
      const { data, error } = await studentDashboardApi().api.student.dashboard['recommended-courses'].get({ query: params })
      if (error) throw error
      return data
    },
    staleTime: CACHE_TIMES.STALE_LIST,
  })
}

export function liveEventsQueryOptions() {
  return queryOptions({
    queryKey: dashboardKeys.liveEvents(),
    queryFn: async () => {
      const { data, error } = await studentDashboardApi().api.student.dashboard['live-events'].get()
      if (error) throw error
      return data
    },
    staleTime: CACHE_TIMES.STALE_REALTIME,
  })
}

export function menuItemsQueryOptions() {
  return queryOptions({
    queryKey: dashboardKeys.menuItems(),
    queryFn: async () => {
      const { data, error } = await studentDashboardApi().api.student.dashboard['menu-items'].get()
      if (error) throw error
      return data
    },
    staleTime: CACHE_TIMES.STALE_STATIC,
    gcTime: CACHE_TIMES.GC_TIME_STATIC,
  })
}

// ============================================================================
// MUTATIONS
// ============================================================================

export function useUpdateCustomerProfile() {
  return useMutation({
    // ⚠️ O tipo é escrito à mão, NÃO inferido do Eden — campo novo no
    // `updateCustomerBodySchema` da api não chega aqui sozinho. Foi por isso
    // que `leaderboardOptOut` typecheckou na api e reprovou no student.
    mutationFn: async (body: {
      firstName?: string
      lastName?: string
      phone?: string
      birthDate?: string
      cpfCnpj?: string
      /** Sair da lista pública de ranking (D4). */
      leaderboardOptOut?: boolean
    }) => {
      const { data, error } = await studentDashboardApi().api.student.dashboard.customer.patch(body)
      if (error) throw error
      return data
    },
  })
}

export function useUpdateAuthorProfile() {
  return useMutation({
    mutationFn: async (body: { name?: string; bio?: string; jobTitle?: string }) => {
      const { data, error } = await studentDashboardApi().api.student.dashboard.author.patch(body)
      if (error) throw error
      return data
    },
  })
}
